import React, { useState } from 'react';
import { useTheme, alpha, Box, Button, Grid2, TextField, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, InputLabel, IconButton } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { useDispatch } from 'react-redux';
import { deleteTeamMember, createTeamMember, fetchTeamMembers, updateTeamMember } from '../../../../store/startup/team/teamSlice';
import SingleFileUpload from '../../../fileUpload/FileUpload';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TeamMemberAccordion = ({ member, onSaved, onMoveUp, onMoveDown, disableMoveUp, disableMoveDown }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(member.isNew || false);
  const [formData, setFormData] = useState({
    firstName: member.firstName || '',
    lastName: member.lastName || '',
    positionTitle: member.positionTitle || '',
    biography: member.biography || '',
    responsibilities: member.responsibilities || '',
    expertise: member.expertise || '',
    relationToStartup: member.relationToStartup || '',
    email: member.email || '',
    instagram: member.instagram || '',
    linkedin: member.linkedin || '',
    profilePhotoPath: member.profilePhotoPath || '',
    resumeFilePath: member.resumeFilePath || ''
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    positionTitle: false,
    biography: false,
    responsibilities: false,
    expertise: false,
    relationToStartup: false,
    email: false,
    profilePhotoPath: false
  });

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleInputChange = (field) => (event) => {
    const newValue = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const handleQuillChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = (filePath) => {
    setFormData(prev => ({
      ...prev,
      profilePhotoPath: filePath
    }));
  };

  const handleCvUpload = (filePath) => {
    setFormData(prev => ({
      ...prev,
      resumeFilePath: filePath
    }));
  };

  const validateForm = (data) => {
    const newErrors = {
      firstName: !data.firstName?.trim() ? "İsim alanı zorunludur" : "",
      lastName: !data.lastName?.trim() ? "Soyisim alanı zorunludur" : "",
      positionTitle: !data.positionTitle?.trim() ? "Pozisyon alanı zorunludur" : "",
      biography: !data.biography?.trim() ? "Biyografi alanı zorunludur" : "",
      responsibilities: !data.responsibilities?.trim() ? "Sorumluluklar alanı zorunludur" : "",
      expertise: !data.expertise?.trim() ? "Uzmanlık alanı zorunludur" : "",
      relationToStartup: !data.relationToStartup?.trim() ? "Girişim ile ilişki alanı zorunludur" : "",
      email: !data.email?.trim()
        ? "Email alanı zorunludur"
        : !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email)
          ? "Geçerli bir email adresi giriniz"
          : "",
    };

    const hasErrors = Object.values(newErrors).some(error => error !== "");

    return { isValid: !hasErrors, errors: newErrors };
  };

  const handleSave = async () => {
    const { isValid, errors: newErrors } = validateForm(formData);
    setErrors(newErrors);

    if (!isValid) {
      toast.error("Lütfen tüm zorunlu alanları doldurun");
      return;
    }

    try {
      if (member.isNew) {
        await dispatch(createTeamMember({
          startupId: member.startupId,
          ...formData
        })).unwrap();
        if (onSaved) onSaved();
      } else {
        await dispatch(updateTeamMember({
          teamMemberId: member.teamMemberId,
          ...formData
        })).unwrap();
      }
      await dispatch(fetchTeamMembers(member.startupId)).unwrap();
      toast.success('Takım üyesi başarıyla güncellendi');
      setExpanded(false);
    } catch (error) {
      console.error('Failed to save team member:', error);
      toast.error("Takım üyesi kaydedilirken bir hata oluştu");
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteTeamMember(member.teamMemberId)).unwrap();
      await dispatch(fetchTeamMembers(member.startupId)).unwrap();
    } catch (error) {
      console.error('Failed to delete team member:', error);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet'
  ];

  return (
    <Box marginTop={5}>
      <Accordion expanded={expanded} onChange={handleChange('panel1')} sx={{ backgroundColor: '#f5f5f5' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{formData.firstName} {formData.lastName}</Typography>
            <IconButton 
              size="small" 
              onClick={onMoveUp}
              disabled={disableMoveUp}
            >
              <ArrowUpward />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={onMoveDown}
              disabled={disableMoveDown}
            >
              <ArrowDownward />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper elevation={3} sx={{ padding: '20px', maxWidth: '800px', margin: 'auto' }}>
            <Grid2 container spacing={3}>
              <Grid2 item size={{ xs: 12, sm: 6 }}>
                <SingleFileUpload onUploadComplete={handlePhotoUpload} existFilePath={formData.profilePhotoPath} fileUploadPath={"startup"} />
              </Grid2>
              <Grid2 item size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Adı"
                  fullWidth
                  variant="outlined"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  required
                />
                <TextField
                  label="Soyadı"
                  fullWidth
                  variant="outlined"
                  sx={{ marginTop: '10px' }}
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  required
                />
                <TextField
                  label="Pozisyonu / Unvanı"
                  fullWidth
                  variant="outlined"
                  sx={{ marginTop: '10px' }}
                  value={formData.positionTitle}
                  onChange={handleInputChange('positionTitle')}
                  error={Boolean(errors.positionTitle)}
                  helperText={errors.positionTitle}
                  required
                />
              </Grid2>

              <Box sx={{ width: '800px',
                mb: 3,
                margin: "0 auto",
                maxWidth: 900,
                '& .quill': {
                  '& .ql-toolbar': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.mode === 'light'
                      ? alpha(theme.palette.grey[100], 0.5)
                      : alpha(theme.palette.grey[800], 0.5),
                    '& .ql-stroke': {
                      stroke: theme.palette.mode === 'light'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300]
                    },
                    '& .ql-fill': {
                      fill: theme.palette.mode === 'light'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300]
                    },
                    '& .ql-picker': {
                      color: theme.palette.text.primary
                    },
                    '& .ql-picker-options': {
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                      boxShadow: theme.shadows[3]
                    }
                  },
                  '& .ql-container': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.background.paper
                  },
                  '& .ql-editor': {
                    minHeight: '200px',
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.text.primary,
                    '&.ql-blank::before': {
                      color: theme.palette.text.disabled,
                      fontStyle: 'normal',
                      content: 'attr(data-placeholder)'
                    }
                  }
                }
              }}>
                <Typography variant="h6" gutterBottom>
                  Kısa Biyografi
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={formData.biography || ''}
                  onChange={(value) => handleQuillChange('biography', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Kısa Biyografi"
                />

                <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                  Şirkette Üstlendiği Görev ve Sorumluluklar
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={formData.responsibilities || ''}
                  onChange={(value) => handleQuillChange('responsibilities', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Şirkette Üstlendiği Görev ve Sorumluluklar"
                />

                <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                Uzmanlık Alanı ve Profesyonel Tecrübesi
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={formData.expertise || ''}
                  onChange={(value) => handleQuillChange('expertise', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Uzmanlık Alanı ve Profesyonel Tecrübesi"
                />

                <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                  Girişimci ile İlişkisinin Kaynağı
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={formData.relationToStartup || ''}
                  onChange={(value) => handleQuillChange('relationToStartup', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Girişimci ile İlişkisinin Kaynağı"
                />
              </Box>
              
              <Grid2 item size={{ xs: 12, sm: 6 }}>
                <InputLabel>Takım Üyesi CV'si</InputLabel>
                <SingleFileUpload onUploadComplete={handleCvUpload} existFilePath={formData.resumeFilePath} fileUploadPath={"startup"} />
              </Grid2>
              <Grid2 item size={{ xs: 12, sm: 6 }}>
                <TextField label="Instagram" fullWidth variant="outlined" InputProps={{ startAdornment: <InstagramIcon /> }} value={formData.instagram} onChange={handleInputChange('instagram')} />
                <TextField
                  label="E-Posta"
                  fullWidth
                  variant="outlined"
                  sx={{ marginTop: '10px' }}
                  InputProps={{ startAdornment: <EmailIcon /> }}
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  required
                />
                <TextField
                  label="LinkedIn"
                  fullWidth
                  variant="outlined"
                  sx={{ marginTop: '10px' }}
                  InputProps={{ startAdornment: <LinkedInIcon /> }}
                  value={formData.linkedin}
                  onChange={handleInputChange('linkedin')}
                  placeholder="https://linkedin.com/in/username"
                />
              </Grid2>
              <Grid2 item size={{ xs: 6 }}>
                <Button variant="outlined" color="error" fullWidth onClick={handleDelete}>
                  Sil
                </Button>
              </Grid2>
              <Grid2 item size={{ xs: 6 }}>
                <Button variant="contained" color="primary" fullWidth onClick={handleSave}>
                  {member.isNew ? 'Kaydet' : 'Güncelle'}
                </Button>
              </Grid2>
              {errors.profilePhotoPath && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  Lütfen profil fotoğrafı yükleyin
                </Typography>
              )}
            </Grid2>
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TeamMemberAccordion;
